import {
  AuthError,
  ClientError,
  ForbiddenError,
  NetworkError,
  parseApiError,
  ServerError,
} from "#lib/errors"

export abstract class BaseSDK {
  constructor(
    protected readonly baseUrl: string,
    protected readonly getAuthHeaders: () => { Authorization: string; "Content-Type": string }
  ) {}

  protected getLocaleHeader(): string {
    const storedLocale = localStorage.getItem("i18nextLng")
    if (storedLocale) {
      return storedLocale
    }
    return navigator.language || "en"
  }

  protected async fetchWithErrorHandling<T>(url: string, options: RequestInit): Promise<T> {
    try {
      const headers = new Headers(options.headers)
      const locale = this.getLocaleHeader()
      headers.set("Accept-Language", locale)

      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (!response.ok) {
        let errorData: unknown
        try {
          errorData = await response.json()
        } catch {
          errorData = {
            message: `HTTP ${response.status}: ${response.statusText}`,
          }
        }

        const errorMessage = parseApiError(errorData)

        if (response.status === 401) {
          throw new AuthError(
            errorMessage || "Authentication failed",
            "AUTHENTICATION_FAILED",
            response.status,
            errorData
          )
        }

        if (response.status === 403) {
          throw new ForbiddenError(
            errorMessage || "Access forbidden",
            "FORBIDDEN",
            response.status,
            errorData
          )
        }

        if (response.status >= 400 && response.status < 500) {
          throw new ClientError(
            errorMessage || "Invalid request",
            "CLIENT_ERROR",
            response.status,
            errorData
          )
        }

        if (response.status >= 500) {
          throw new ServerError(
            errorMessage || "Server error",
            "SERVER_ERROR",
            response.status,
            errorData
          )
        }

        throw new ClientError(
          errorMessage || "Request failed",
          "REQUEST_FAILED",
          response.status,
          errorData
        )
      }

      const data = await response.json()

      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const errorMessage = parseApiError(data)
        throw new ClientError(errorMessage, "API_ERROR", response.status, data)
      }

      return data as T
    } catch (error) {
      if (
        error instanceof AuthError ||
        error instanceof ForbiddenError ||
        error instanceof ClientError ||
        error instanceof ServerError
      ) {
        throw error
      }

      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new NetworkError("Network request failed. Please check your connection.", error)
      }

      if (error instanceof Error) {
        throw new ClientError(error.message, "UNKNOWN_ERROR", 500, error)
      }

      throw new ClientError("An unknown error occurred", "UNKNOWN_ERROR", 500, error)
    }
  }

  protected extractData<T>(response: unknown): T {
    if (typeof response === "object" && response !== null && "data" in response) {
      return (response as { data: T }).data
    }
    return response as T
  }
}
